import { useCallback } from 'react';

import { BulkDownloadFiles } from '~/utils/DownloadFile';
import { createImgixImageUrl, extIsSupportedExportFormat, type ImgixCropAnchor } from '~/utils/Imgix';

const BATCH_EXPORT_STAGGER_MS = 300;

const CORNER_CROP_ANCHOR: ImgixCropAnchor = 'top,left';

export type ResizeMode = 'scale' | 'exactSize';

export type CropAnchor = 'center' | 'corner';

export type ExportSizeParams = {
  resizeMode: ResizeMode;
  scale: string;
  targetWidth: number;
  targetHeight: number;
  cropAnchor: CropAnchor;
  longestSide?: number;
};

export type ExportDimensions = {
  width: number;
  height: number;
  wasCapped: boolean;
};

export type BatchExportClip = {
  id: string;
  fileName: string;
  width: number;
  height: number;
  imageSrc: string;
};

export type BatchExportImagesParams = ExportSizeParams & {
  suffix: string;
  format: string;
};

/**
 * Computes the output dimensions for a single asset given the chosen resize mode.
 *
 * In `exactSize` mode we crop the source to the target ratio and scale to the target
 * size, but never upscale — assets smaller than the target export at their capped size.
 */
export const calculateExportDimensions = (
  clipWidth: number,
  clipHeight: number,
  params: ExportSizeParams,
): ExportDimensions => {
  if (params.resizeMode === 'scale') {
    if (params.longestSide !== undefined && params.longestSide > 0) {
      const currentLongest = Math.max(clipWidth, clipHeight);
      const rawFactor = params.longestSide / currentLongest;
      const scaleFactor = Math.min(1, rawFactor);

      return {
        width: Math.max(1, Math.round(clipWidth * scaleFactor)),
        height: Math.max(1, Math.round(clipHeight * scaleFactor)),
        wasCapped: rawFactor > 1,
      };
    }

    const multiplier = Number(params.scale);

    return {
      width: Math.max(1, Math.round(clipWidth * multiplier)),
      height: Math.max(1, Math.round(clipHeight * multiplier)),
      wasCapped: false,
    };
  }

  const { targetWidth, targetHeight } = params;

  // Keep the function total: the form prevents non-positive targets, but guard anyway
  // so we never divide by zero or produce non-finite dimensions.
  if (targetWidth <= 0 || targetHeight <= 0) {
    return { width: clipWidth, height: clipHeight, wasCapped: false };
  }

  const targetRatio = targetWidth / targetHeight;
  const sourceRatio = clipWidth / clipHeight;

  let cropMaxWidth: number;

  if (sourceRatio > targetRatio) {
    // source is wider than target ratio → the crop is limited by source height
    cropMaxWidth = clipHeight * targetRatio;
  } else {
    cropMaxWidth = clipWidth;
  }

  // <1 means the source was too small to hit the target, so we cap at original (no upscale).
  const scaleFactor = Math.min(1, cropMaxWidth / targetWidth);
  const width = Math.max(1, Math.round(targetWidth * scaleFactor));
  const height = Math.max(1, Math.round(targetHeight * scaleFactor));
  const wasCapped = scaleFactor < 1;

  return { width, height, wasCapped };
};

export const useBatchExportImages = () => {
  const exportImages = useCallback((clips: BatchExportClip[], params: BatchExportImagesParams) => {
    const { suffix, format } = params;

    if (!extIsSupportedExportFormat(format)) {
      return;
    }

    clips.forEach((clip, index) => {
      const fileName = suffix
        ? `${clip.fileName}_${suffix}_${index + 1}.${format}`
        : `${clip.fileName}_${index + 1}.${format}`;

      const { width, height } = calculateExportDimensions(clip.width, clip.height, params);

      // imgix center-crops by default when fit=crop, so only the corner anchor needs an explicit
      // crop param; passing crop=center would be an invalid imgix value.
      const shouldCropFromCorner = params.resizeMode === 'exactSize' && params.cropAnchor === 'corner';

      const url = createImgixImageUrl({
        imageSrc: clip.imageSrc,
        width,
        height,
        format,
        fileName,
        ...(shouldCropFromCorner ? { crop: CORNER_CROP_ANCHOR } : {}),
      });

      // Intentional fire-and-forget: the modal closes immediately after exportImages() is
      // called, so timeouts must outlive the component. Downloads complete within seconds.
      window.setTimeout(() => {
        BulkDownloadFiles([url]);
      }, index * BATCH_EXPORT_STAGGER_MS);
    });
  }, []);

  return { exportImages };
};
